import {Injectable, NgZone} from "@angular/core";
import {fromEvent, interval, merge, Subject, Subscription} from "rxjs";
import {takeUntil, throttleTime} from "rxjs/operators";
import {EcaseHttpService} from "./ecase-http.service";
import {HttpActivityService} from "./http-activity.service";
import {LoginAuthenticationService} from "./login-authentication.service";

type KeepAliveLeader = { tabId: string; timestamp: number };

@Injectable({
  providedIn: "root"
})
export class SessionKeepAliveService {
  private destroy$ = new Subject<void>();
  private trackingDestroy$ = new Subject<void>();

  private activityDetected = false;
  private started = false;
  private trackingStarted = false;
  private sessionExpired = false;

  private authSubscription: Subscription;

  private readonly CHECK_INTERVAL_MS = 30000;
  private readonly ACTIVITY_THROTTLE_MS = 1000;
  private readonly ACTIVITY_KICK_THRESHOLD_MS = 40000;
  private lastExtendSentAt = 0;

  private tabId = this.generateTabId();
  private readonly LEADER_KEY = 'synto_keepalive_leader';
  private readonly LEADER_TIMEOUT = 40000; // 40 sec
  private readonly HTTP_IDLE_MS = 5000;

  constructor(
    private loginService: LoginAuthenticationService,
    private http: EcaseHttpService,
    private httpActivityService: HttpActivityService,
    private ngZone: NgZone
  ) {
  }

  public init(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.authSubscription = this.loginService.isUserLoggedIn$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {
        if (isLoggedIn) {
          this.sessionExpired = false;
          this.startTracking();
        } else {
          this.stopTracking();
        }
      });

    window.addEventListener("beforeunload", () => {
      const raw = localStorage.getItem(this.LEADER_KEY);
      if (!raw) {
        return;
      }
      let leader: KeepAliveLeader;
      try {
        leader = JSON.parse(raw);
      } catch {
        localStorage.removeItem(this.LEADER_KEY);
        return;
      }
      if (leader.tabId === this.tabId) {
        localStorage.removeItem(this.LEADER_KEY);
      }
    });
  }

  private startTracking(): void {
    if (this.trackingStarted) {
      return;
    }
    this.trackingStarted = true;

    this.lastExtendSentAt = Date.now();


    this.trackingDestroy$ = new Subject<void>();

    this.ngZone.runOutsideAngular(() => {
      const activity$ = merge(
        fromEvent(window, "mousemove"),
        fromEvent(window, "mousedown"),
        fromEvent(window, "keydown"),
        fromEvent(window, "scroll"),
        fromEvent(window, "touchstart"),
        fromEvent(window, "click")
      ).pipe(
        throttleTime(this.ACTIVITY_THROTTLE_MS),
        takeUntil(this.trackingDestroy$)
      );

      activity$.subscribe(() => {
        this.activityDetected = true;

        const elapsedSinceLastExtend = Date.now() - this.lastExtendSentAt;
        if (
          document.visibilityState === 'visible' &&
          elapsedSinceLastExtend > this.ACTIVITY_KICK_THRESHOLD_MS &&
          this.isLeader()
        ) {
          this.activityDetected = false;
          this.ngZone.run(() => {
            this.extendSession();
          });
        }
      });

      interval(this.CHECK_INTERVAL_MS)
        .pipe(takeUntil(this.trackingDestroy$))
        .subscribe(() => {
          if (document.visibilityState !== "visible") {
            return;
          }
          if (!this.activityDetected) {
            return;
          }
          if (!this.isLeader()) {
            return;
          }
          this.ngZone.run(() => {
            this.extendSession();
            this.activityDetected = false;
          });
        });

      interval(this.CHECK_INTERVAL_MS / 2)
        .pipe(takeUntil(this.trackingDestroy$))
        .subscribe(() => {
          if (this.isLeader()) {
            this.becomeLeader(Date.now());
          }
        });
    });
  }

  private stopTracking(): void {
    this.activityDetected = false;
    this.trackingStarted = false;

    if (this.trackingDestroy$) {
      this.trackingDestroy$.next();
      this.trackingDestroy$.complete();
    }
  }

  private extendSession(): void {
    if (this.sessionExpired) {
      console.trace("Cannot extend session, session is expired.");
      return;
    }
    if (!this.httpActivityService.isIdleFor(this.HTTP_IDLE_MS)) {
      console.trace("Cannot extend session, HTTP activity is ongoing.");
      return;
    }

    this.lastExtendSentAt = Date.now();
    this.http.post("/api/extendSession", {})
      .subscribe({
        error: (err) => {
          if (err?.status === 401 || err?.errorCode === 401) {
            this.sessionExpired = true;
            // Stop intervals immediately
            this.stopTracking();
            this.ngZone.run(() => {
              this.http.checkForSessionTimout({status: true, counter: 0});
            });
            return;
          }
          // Silent fail – do not spam logs
        }
      });
  }

  private generateTabId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  private isLeader(): boolean {
    const now = Date.now();
    const raw = localStorage.getItem(this.LEADER_KEY);

    if (!raw) {
      this.becomeLeader(now);
      return true;
    }
    let leader: KeepAliveLeader;
    try {
      leader = JSON.parse(raw);
    } catch {
      this.becomeLeader(now);
      return true;
    }
    if (!leader?.tabId || typeof leader.timestamp !== "number") {
      this.becomeLeader(now);
      return true;
    }
    if (leader.tabId === this.tabId) {
      this.becomeLeader(now);
      return true;
    }
    if (now - leader.timestamp > this.LEADER_TIMEOUT) {
      this.becomeLeader(now);
      return true;
    }
    return false;
  }

  private becomeLeader(timestamp: number): void {
    localStorage.setItem(this.LEADER_KEY, JSON.stringify({
      tabId: this.tabId,
      timestamp: timestamp
    }));
  }

  public destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }

    this.stopTracking();
  }
}
